import React from "react";
import {ActionRow, ActionRowButton, Modal, TableData, useNotifications} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {DeliveryArea, DeliveryAreaFormValues, UpdateDeliveryAreaFields} from "@/data/entities/deliveryArea";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {SetStateFn} from "@/utils/SetStateUtils";
import {FormProvider, useForm} from "react-hook-form";
import {useMutation} from "@tanstack/react-query";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import EditDeliveryAreaForm from "@widgets/deliveryAreas/edit/EditDeliveryAreaForm";
import {api} from "@/data/api";

export type EditDeliveryAreaState = {
    showModal: boolean,
    deliveryArea: DeliveryArea | null,
};

type Props = {
    show: boolean,
    setShow: (show: boolean) => void,
    deliveryArea: DeliveryArea | null,
    setTableData: SetStateFn<TableData<DeliveryArea>[]>,
};

const EditDeliveryAreaModal = ({show, setShow, deliveryArea, setTableData}: Props) => {
    const intl = useIntl();
    const {dashboardLocation} = useRestaurant();
    const {notify} = useNotifications();
    const formMethods = useForm<DeliveryAreaFormValues>();
    const {mutate: upsertDeliveryArea, isPending} = useMutation({
        mutationFn: async (form: DeliveryAreaFormValues): Promise<DeliveryArea[]> => {
            const fields: UpdateDeliveryAreaFields = {
                ...form,
                minimumOrderAmount: Number(form.minimumOrderAmount.replace(",", ".")),
                deliveryFee: Number(form.deliveryFee.replace(",", ".")),
            }
            return deliveryArea === null
                ? api.deliveryArea.createDeliveryArea(dashboardLocation.id, fields)
                : api.deliveryArea.updateDeliveryArea(dashboardLocation.id, deliveryArea.id, fields)
        },
        onSuccess: (updatedAreas: DeliveryArea[]) => {
            setTableData(updatedAreas);
            setShow(false);
            notifySuccess(intl.formatMessage({id: "saved"}), notify);
        },
        onError: () => notifyDanger(intl.formatMessage({id: "systemError"}), notify),
    });
    const handleSubmitHandler: (() => void) = formMethods.handleSubmit((data: DeliveryAreaFormValues) => {
        upsertDeliveryArea(data);
    });

    return (
        <Modal
            show={show}
            onClose={() => setShow(false)}
            title={intl.formatMessage({id: deliveryArea === null ? "newDeliveryArea" : "editDeliveryArea"})}
            actionRow={
                <ActionRow>
                    <ActionRowButton
                        color={"secondary"}
                        disabled={isPending}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    setShow(false);
                                }
                            },
                        }}
                    >
                        <FormattedMessage id={"cancel"}/>
                    </ActionRowButton>
                    <ActionRowButton
                        disabled={isPending}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    handleSubmitHandler();
                                }
                            },
                        }}
                    >
                        <FormattedMessage id={"saveAndClose"}/>
                    </ActionRowButton>
                </ActionRow>
            }
            disabled={isPending}
            loading={isPending}
            position={"center-bottom"}
            breakpoints={{md: {position: "center"}}}
        >
            <FormProvider {...formMethods}>
                <EditDeliveryAreaForm
                    deliveryArea={deliveryArea}
                    handleSubmitHandler={handleSubmitHandler}
                    disabledState={isPending}
                />
            </FormProvider>
        </Modal>
    );
};

export default EditDeliveryAreaModal;